import { makeVar } from '@apollo/client'
import { userVar } from './user'

const STORAGE_KEY_PREFIX = 'xyro:play-wallet:'

/**
 * The linked wallet the user picked to play with. A client-side preference:
 * game transactions are signed only when the connected wallet is this one.
 */
export const selectedWalletAddressVar = makeVar<string | null>(null)

export enum PlayWalletStatus {
  ready = 'ready',
  notLinked = 'notLinked',
  notSelected = 'notSelected'
}

export const readStoredPlayWallet = (userId: string): string | null => {
  try {
    return localStorage.getItem(`${STORAGE_KEY_PREFIX}${userId}`)
  } catch {
    return null
  }
}

export const storePlayWallet = (userId: string, address: string) => {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${userId}`, address)
  } catch {
    // NOTE: storage refused the write; the choice lives until the page reloads
  }
}

/** Remembers `address` as the user's wallet for play and selects it. */
export const choosePlayWallet = (userId: string, address: string) => {
  storePlayWallet(userId, address)
  selectedWalletAddressVar(address)
}

/**
 * The wallet to play with: the stored choice when it is still linked,
 * otherwise the connected wallet when it is linked, otherwise the primary one.
 */
export const resolvePlayWallet = (
  wallets: { address: string; isPrimary: boolean }[],
  storedAddress: string | null,
  connectedAddress: string | null
): string | null => {
  const linkedAddresses = wallets.map(wallet => wallet.address)

  if (storedAddress && linkedAddresses.includes(storedAddress)) {
    return storedAddress
  }
  if (connectedAddress && linkedAddresses.includes(connectedAddress)) {
    return connectedAddress
  }

  const primaryWallet = wallets.find(wallet => wallet.isPrimary)
  return primaryWallet?.address ?? linkedAddresses[0] ?? null
}

export const getPlayWalletStatus = (
  connectedAddress: string,
  linkedAddresses: string[],
  selectedAddress: string | null
): PlayWalletStatus => {
  if (!linkedAddresses.includes(connectedAddress)) {
    return PlayWalletStatus.notLinked
  }
  if (connectedAddress !== selectedAddress) return PlayWalletStatus.notSelected
  return PlayWalletStatus.ready
}

/**
 * Last line of defence before a game or treasury transaction is signed.
 * On-chain activity from a wallet that is not linked to the account is
 * attributed to an auto-created Anonymous user, and that wallet can then never
 * be linked, so such a transaction must never reach the wallet for signing.
 */
export const assertPlayWallet = (connectedAddress: string | undefined) => {
  const linkedAddresses = userVar()?.wallets.map(wallet => wallet.address) ?? []

  if (!connectedAddress) throw new Error('Wallet not connected')

  const status = getPlayWalletStatus(
    connectedAddress,
    linkedAddresses,
    selectedWalletAddressVar()
  )

  if (status === PlayWalletStatus.notLinked) {
    throw new Error(
      'The connected wallet is not linked to your account. Link it before playing.'
    )
  }
  if (status === PlayWalletStatus.notSelected) {
    throw new Error(
      'The connected wallet is not the one selected for play. Switch wallets before playing.'
    )
  }
}
